import React, {useState, useEffect} from "react";
import {
    Box,
    Text,
    Textarea,
    Button,
    useColorModeValue,
    useToast,
    Flex,
    Spinner,
    Stack, Select
} from "@chakra-ui/react";
import {cqAPI} from "../../lib/Auth";

const VAVPrompts = () => {
    const bgColor = useColorModeValue("gray.100", "gray.700");
    const cardBg = useColorModeValue("#ffffff", "#212330");
    const borderClr = useColorModeValue("gray.200", "gray.600");

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const defaultFormData = {
        answer_first_grounding_note: "",
        converse_with_patient_grounding_note: "",
        failed_verification: "",
        need_to_verify: "",
        security_prompt: "",
        info_collection_intro_prompt: "",
        responder_security_prompt: "",
        responder_security_prompt_for_transfer_section: "",
        responder_output_prompt: "",
        appointment_agent_prompts: {
            cant_modify_once_completed: "",
            end_of_action_grounding: "",
            insurance_llm_note: "",
            grounding_collected_data_prompt: "",
            info_collection_intro_prompt: "",
            intro_prompt_no_pms: "",
            intro_prompt: "",
            interaction_guideline_prompt: "",
            no_upcoming_appointment_note: "",
            processing_appointment_note: "",
            apt_detail_confirmation_denied_note: "",
            no_service_note: ""
        },
        appointment_booker_prompts: {
            booking_status_section: "",
            core_responsibility_section: "",
            demo_services_section: "",
            demo_availabilities_section: "",
            availability_based_on_reason_section: "",
            no_availability_section: ""
        },
        appointment_confirmer_prompts: {
            core_responsibility_section: "",
            confirmer_status_section: "",
            edge_case_section: ""
        },
        appointment_canceller_prompts: {
            core_responsibility_section: "",
            cancellation_status_section: "",
            edge_case_section: ""
        },
        appointment_rescheduler_prompts: {
            core_responsibility_section: "",
            rescheduler_status_section: "",
            edge_case_section: ""
        },
        call_transfer_prompts: {
            main_prompt_section: "",
            confirm_transfer_section: "",
            who_to_transfer_section: ""
        },
        data_extractor_prompts: {
            intro_section: "",
            llm_input_section: ""
        },
        general_agent_prompts: {
            intro_section: "",
            pms_section: "",
            demo_mode_section: "",
            guideline_section: ""
        },
        health_agent_prompts: {
            base_section: "",
            core_responsibility_section: "",
            guideline_section: "",
            incomplete_treatment_section: "",
            no_dental_score_section: "",
            need_to_verify_section: ""
        },
        moderator_prompts: {
            base_section: "",
            grounding_section: ""
        },
        summarizer_prompts: {
            base_section: "",
            edge_case_section: ""
        },
        business_router_prompts: {
            main_prompt_section: "",
            confirm_transfer_section: "",
            clarify_business_section: ""
        },
        default_llm: "cerebras",
        backup_llm: "groq"
    };


    const [formData, setFormData] = useState(defaultFormData);
    const toast = useToast();

    // ----------- LOAD PROMPTS -----------
    useEffect(() => {
        const fetchVAVPrompts = async () => {
            setLoading(true);
            try {
                const response = await cqAPI.get("/prompts/vav");
                if (response.type === "ok" && response.data) {
                    setFormData(response.data);
                } else {
                    console.warn("Invalid or missing response data, using defaults");
                    setFormData(defaultFormData);
                    toast({
                        title: "Warning",
                        description: "Loaded default VAV Prompts because the API response was invalid.",
                        status: "warning",
                        duration: 3000,
                        isClosable: true,
                    });
                }
            } catch (error) {
                console.error("Error loading VAV Prompt:", error);
                toast({
                    title: "Error",
                    description: "Failed to load VAV Prompt.",
                    status: "error",
                    duration: 3000,
                    isClosable: true
                });
            } finally {
                setLoading(false);
            }
        };
        fetchVAVPrompts();
    }, []);

    // ----------- SAVE PROMPTS -----------
    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await cqAPI.put("/prompts/vav", formData);
            if (response.type === "ok") {
                toast({
                    title: "Saved",
                    description: "VAV Prompts updated successfully.",
                    status: "success",
                    duration: 3000,
                    isClosable: true
                });
            } else {
                toast({
                    title: "Error",
                    description: response.msg || 'Failed to save!',
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error("Error saving VAV Prompt:", error);
            toast({
                title: "Error",
                description: "Failed to save VAV Prompt.",
                status: "error",
                duration: 3000,
                isClosable: true
            });
        } finally {
            setSaving(false);
        }
    };

    // ----------- RENDER UI -----------
    return (
        <Box bg={bgColor} minH="100dvh" w="100%">
            <Box bg={bgColor} p={6} borderRadius="md" w="100%" maxW="100%">
                <Flex justify="space-between" align="center" mb={6}>
                    <Text fontSize="xl" fontWeight="bold">
                        VAV Prompts
                    </Text>
                    <Button
                        colorScheme="blue"
                        onClick={handleSave}
                        isLoading={saving}
                        loadingText="Saving"
                    >
                        Save
                    </Button>
                </Flex>

                {loading ? (
                    <Flex justify="center" align="center" h="200px">
                        <Spinner size="xl"/>
                    </Flex>
                ) : (
                    <Stack spacing={6}>
                        {/* LLM selectors */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={3}>
                          LLM Settings
                        </Text>

                        <Stack direction={{ base: "column", md: "row" }} spacing={4}>
                          <Box flex="1">
                            <Text fontSize="sm" mb={2} color="gray.600">
                              Default LLM
                            </Text>
                            <Select
                              value={formData.default_llm ?? ""}
                              onChange={(e) => setFormData({ ...formData, default_llm: e.target.value })}
                              placeholder="Select default LLM"
                            >
                              <option value="cerebras">cerebras</option>
                              <option value="groq">groq</option>
                              <option value="oai">oai</option>
                            </Select>
                          </Box>

                          <Box flex="1">
                            <Text fontSize="sm" mb={2} color="gray.600">
                              Backup LLM
                            </Text>
                            <Select
                              value={formData.backup_llm ?? ""}
                              onChange={(e) => setFormData({ ...formData, backup_llm: e.target.value })}
                              placeholder="Select backup LLM"
                            >
                              <option value="cerebras">cerebras</option>
                              <option value="groq">groq</option>
                              <option value="oai">oai</option>
                            </Select>
                          </Box>
                        </Stack>
                        </Box>
                        {/* Answer First grounding note */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Answer First Grounding Note
                            </Text>
                            <Textarea
                                minH="120px"
                                resize="vertical"
                                value={formData.answer_first_grounding_note}
                                onChange={(e) =>
                                    setFormData({...formData, answer_first_grounding_note: e.target.value})
                                }
                                placeholder="Answer First grounding note..."
                            />
                        </Box>

                        {/* converse with patient grounding note */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Converse with Patient Grounding Note
                            </Text>
                            <Textarea
                                minH="120px"
                                resize="vertical"
                                value={formData.converse_with_patient_grounding_note}
                                onChange={(e) =>
                                    setFormData({...formData, converse_with_patient_grounding_note: e.target.value})
                                }
                                placeholder="Converse with patient grounding note..."
                            />
                        </Box>

                        {/* Failed Verification */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Failed Verification
                            </Text>
                            <Textarea
                                minH="120px"
                                resize="vertical"
                                value={formData.failed_verification}
                                onChange={(e) =>
                                    setFormData({...formData, failed_verification: e.target.value})
                                }
                                placeholder="Failed Verification..."
                            />
                        </Box>

                        {/* Need To Verify */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Need To Verify
                            </Text>
                            <Textarea
                                minH="120px"
                                resize="vertical"
                                value={formData.need_to_verify}
                                onChange={(e) =>
                                    setFormData({...formData, need_to_verify: e.target.value})
                                }
                                placeholder="Need To Verify..."
                            />
                        </Box>

                        {/* security prompt */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Security Prompt
                            </Text>
                            <Textarea
                                minH="120px"
                                resize="vertical"
                                value={formData.security_prompt}
                                onChange={(e) =>
                                    setFormData({...formData, security_prompt: e.target.value})
                                }
                                placeholder="Security Prompt..."
                            />
                        </Box>

                        {/* Info Collection Intro Prompt */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Info Collection Intro Prompt
                            </Text>
                            <Textarea
                                minH="120px"
                                resize="vertical"
                                value={formData.info_collection_intro_prompt}
                                onChange={(e) =>
                                    setFormData({...formData, info_collection_intro_prompt: e.target.value})
                                }
                                placeholder="Info Collection Intro Prompt..."
                            />
                        </Box>

                        {/* Responder Security Prompt */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Responder Security Prompt
                            </Text>
                            <Textarea
                                minH="120px"
                                resize="vertical"
                                value={formData.responder_security_prompt}
                                onChange={(e) =>
                                    setFormData({...formData, responder_security_prompt: e.target.value})
                                }
                                placeholder="Responder Security Prompt..."
                            />
                        </Box>

                        {/* Responder Security prompt for transfer Section */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Responder Security Prompt For Transfer Section
                            </Text>
                            <Textarea
                                minH="120px"
                                resize="vertical"
                                value={formData.responder_security_prompt_for_transfer_section}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        responder_security_prompt_for_transfer_section: e.target.value
                                    })
                                }
                                placeholder="Responder Security prompt for transfer Section..."
                            />
                        </Box>

                        {/* Responder Output Prompt */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Responder Output Prompt
                            </Text>
                            <Textarea
                                minH="120px"
                                resize="vertical"
                                value={formData.responder_output_prompt}
                                onChange={(e) =>
                                    setFormData({...formData, responder_output_prompt: e.target.value})
                                }
                                placeholder="Responder Output Prompt..."
                            />
                        </Box>

                        {/* Appointment Agent Prompt */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Appointment Agent Prompt
                            </Text>
                            <Stack spacing={3}>
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Can't Modify Once Completed
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_agent_prompts.cant_modify_once_completed}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_agent_prompts: {
                                                ...formData.appointment_agent_prompts,
                                                cant_modify_once_completed: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Agent Prompt Can't modify once completed..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    End of Action grounding
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_agent_prompts.end_of_action_grounding}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_agent_prompts: {
                                                ...formData.appointment_agent_prompts,
                                                end_of_action_grounding: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Agent Prompt End of Action Grounding..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Insurance LLM Note
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_agent_prompts.insurance_llm_note}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_agent_prompts: {
                                                ...formData.appointment_agent_prompts,
                                                insurance_llm_note: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Agent Prompt Insurance LLM Note..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Grounding Collected Data Prompt
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_agent_prompts.grounding_collected_data_prompt}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_agent_prompts: {
                                                ...formData.appointment_agent_prompts,
                                                grounding_collected_data_prompt: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Agent Prompt Grounding Collected Data Prompt..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Info Collection Intro Prompt
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_agent_prompts.info_collection_intro_prompt}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_agent_prompts: {
                                                ...formData.appointment_agent_prompts,
                                                info_collection_intro_prompt: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Agent Prompt Info Collection Intro Prompt..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Intro Prompt No PMS
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_agent_prompts.intro_prompt_no_pms}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_agent_prompts: {
                                                ...formData.appointment_agent_prompts,
                                                intro_prompt_no_pms: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Agent Prompt Info Prompt No PMS..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Intro Prompt
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_agent_prompts.intro_prompt}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_agent_prompts: {
                                                ...formData.appointment_agent_prompts,
                                                intro_prompt: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Agent Prompt Intro Prompt..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Interaction Guideline Prompt
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_agent_prompts.interaction_guideline_prompt}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_agent_prompts: {
                                                ...formData.appointment_agent_prompts,
                                                interaction_guideline_prompt: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Agent Prompt Interaction Guideline Prompt..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    No Upcoming Appointment Note
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_agent_prompts.no_upcoming_appointment_note}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_agent_prompts: {
                                                ...formData.appointment_agent_prompts,
                                                no_upcoming_appointment_note: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Agent Prompt No Upcoming Appointment Note..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                  Processing Appointment Note
                                </Text>
                                <Textarea
                                  minH="100px"
                                  resize="vertical"
                                  value={formData.appointment_agent_prompts.processing_appointment_note}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      appointment_agent_prompts: {
                                        ...formData.appointment_agent_prompts,
                                        processing_appointment_note: e.target.value,
                                      },
                                    })
                                  }
                                  placeholder="Appointment Agent Prompt Processing Appointment Note..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                  Appointment Detail Confirmation Denied Note
                                </Text>
                                <Textarea
                                  minH="100px"
                                  resize="vertical"
                                  value={formData.appointment_agent_prompts.apt_detail_confirmation_denied_note}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      appointment_agent_prompts: {
                                        ...formData.appointment_agent_prompts,
                                        apt_detail_confirmation_denied_note: e.target.value,
                                      },
                                    })
                                  }
                                  placeholder="Appointment Agent Prompt Appointment Detail Confirmation Denied Note..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                  No Service for patient
                                </Text>
                                <Textarea
                                  minH="100px"
                                  resize="vertical"
                                  value={formData.appointment_agent_prompts.no_service_note}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      appointment_agent_prompts: {
                                        ...formData.appointment_agent_prompts,
                                        no_service_note: e.target.value,
                                      },
                                    })
                                  }
                                  placeholder="Appointment Agent Prompt No service for patient note..."
                                />
                            </Stack>

                        </Box>

                        {/* Appointment Booker Prompt */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Appointment Booker Prompt
                            </Text>
                            <Stack spacing={3}>
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Booking Status Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_booker_prompts.booking_status_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_booker_prompts: {
                                                ...formData.appointment_booker_prompts,
                                                booking_status_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Booker Prompt Booking Status Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Core Responsibility Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_booker_prompts.core_responsibility_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_booker_prompts: {
                                                ...formData.appointment_booker_prompts,
                                                core_responsibility_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Booker Prompt Core Responsibility Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Demo Services Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_booker_prompts.demo_services_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_booker_prompts: {
                                                ...formData.appointment_booker_prompts,
                                                demo_services_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Booker Prompt Demo Services Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Demo Availabilities Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_booker_prompts.demo_availabilities_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_booker_prompts: {
                                                ...formData.appointment_booker_prompts,
                                                demo_availabilities_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Booker Prompt Demo Availabilities Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Availability Based on Reason Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_booker_prompts.availability_based_on_reason_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_booker_prompts: {
                                                ...formData.appointment_booker_prompts,
                                                availability_based_on_reason_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Booker Prompt Availability based on reason Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    No Availability Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_booker_prompts.no_availability_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_booker_prompts: {
                                                ...formData.appointment_booker_prompts,
                                                no_availability_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Booker Prompt No Availability Section..."
                                />
                            </Stack>
                        </Box>

                        {/* Appointment Confirmer Prompt */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Appointment Confirmer Prompt
                            </Text>
                            <Stack spacing={3}>
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Core Responsibility Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_confirmer_prompts.core_responsibility_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_confirmer_prompts: {
                                                ...formData.appointment_confirmer_prompts,
                                                core_responsibility_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Confirmer Prompt Core Responsibility Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Confirmer Status Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_confirmer_prompts.confirmer_status_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_confirmer_prompts: {
                                                ...formData.appointment_confirmer_prompts,
                                                confirmer_status_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Confirmer Prompt Confirmer Status Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Edge Case Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_confirmer_prompts.edge_case_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_confirmer_prompts: {
                                                ...formData.appointment_confirmer_prompts,
                                                edge_case_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Confirmer Prompt Edge Case Section..."
                                />
                            </Stack>
                        </Box>

                        {/* Appointment Canceller Prompt */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Appointment Canceller Prompt
                            </Text>
                            <Stack spacing={3}>
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Core Responsibility Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_canceller_prompts.core_responsibility_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_canceller_prompts: {
                                                ...formData.appointment_canceller_prompts,
                                                core_responsibility_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Canceller Prompt Core Responsibility Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Collection Status Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_canceller_prompts.cancellation_status_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_canceller_prompts: {
                                                ...formData.appointment_canceller_prompts,
                                                cancellation_status_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Canceller Prompt Cancellation Status Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Edge Case Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_canceller_prompts.edge_case_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_canceller_prompts: {
                                                ...formData.appointment_canceller_prompts,
                                                edge_case_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Canceller Prompt Edge Case Section..."
                                />
                            </Stack>
                        </Box>

                        {/* Appointment Rescheduler Prompt */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Appointment Rescheduler Prompt
                            </Text>
                            <Stack spacing={3}>
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Core responsibility Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_rescheduler_prompts.core_responsibility_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_rescheduler_prompts: {
                                                ...formData.appointment_rescheduler_prompts,
                                                core_responsibility_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Rescheduler Prompt Core Responsibility Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Rescheduler Status Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_rescheduler_prompts.rescheduler_status_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_rescheduler_prompts: {
                                                ...formData.appointment_rescheduler_prompts,
                                                rescheduler_status_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Rescheduler Prompt Rescheduler Status Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Edge Case Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.appointment_rescheduler_prompts.edge_case_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            appointment_rescheduler_prompts: {
                                                ...formData.appointment_rescheduler_prompts,
                                                edge_case_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Appointment Rescheduler Prompt Edge Case Section..."
                                />
                            </Stack>
                        </Box>

                        {/* Call Transfer Prompts */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Call Transfer Prompts
                            </Text>
                            <Stack spacing={3}>
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Main Prompt Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.call_transfer_prompts.main_prompt_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            call_transfer_prompts: {
                                                ...formData.call_transfer_prompts,
                                                main_prompt_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Call Transfer Prompts Main Prompt Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Confirm Transfer Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.call_transfer_prompts.confirm_transfer_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            call_transfer_prompts: {
                                                ...formData.call_transfer_prompts,
                                                confirm_transfer_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Call Transfer Prompts Confirm Transfer Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Who to Transfer Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.call_transfer_prompts.who_to_transfer_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            call_transfer_prompts: {
                                                ...formData.call_transfer_prompts,
                                                who_to_transfer_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Call Transfer Prompts Who To Transfer Section..."
                                />
                            </Stack>
                        </Box>

                        {/* Data Extractor Prompts */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Data Extractor Prompts
                            </Text>

                            <Stack spacing={3}>
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Intro Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.data_extractor_prompts.intro_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            data_extractor_prompts: {
                                                ...formData.data_extractor_prompts,
                                                intro_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Data Extractor Prompts Intro Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    LLM Input Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.data_extractor_prompts.llm_input_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            data_extractor_prompts: {
                                                ...formData.data_extractor_prompts,
                                                llm_input_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Data Extractor Prompts LLM Input Section ..."
                                />
                            </Stack>
                        </Box>

                        {/* General Agents Prompts */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                General Agents Prompts
                            </Text>
                            <Stack spacing={3}>
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Intro Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.general_agent_prompts.intro_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            general_agent_prompts: {
                                                ...formData.general_agent_prompts,
                                                intro_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="General Agents Prompts Intro Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    PMS Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.general_agent_prompts.pms_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            general_agent_prompts: {
                                                ...formData.general_agent_prompts,
                                                pms_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="General Agents Prompts PMS Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Demo mode Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.general_agent_prompts.demo_mode_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            general_agent_prompts: {
                                                ...formData.general_agent_prompts,
                                                demo_mode_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="General Agents Prompts Demo Mode Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Guideline Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.general_agent_prompts.guideline_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            general_agent_prompts: {
                                                ...formData.general_agent_prompts,
                                                guideline_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="General Agents Prompts Guideline Section..."
                                />
                            </Stack>
                        </Box>

                        {/* Health Agents Prompts */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Health Agents Prompts
                            </Text>
                            <Stack spacing={3}>
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Base Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.health_agent_prompts.base_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            health_agent_prompts: {
                                                ...formData.health_agent_prompts,
                                                base_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Health Agents Prompts Base Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Core Responsibility Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.health_agent_prompts.core_responsibility_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            health_agent_prompts: {
                                                ...formData.health_agent_prompts,
                                                core_responsibility_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Health Agents Prompts Core Responsibility Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Guideline Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.health_agent_prompts.guideline_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            health_agent_prompts: {
                                                ...formData.health_agent_prompts,
                                                guideline_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Health Agents Prompts Core Guideline Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Incomplete Treatment Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.health_agent_prompts.incomplete_treatment_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            health_agent_prompts: {
                                                ...formData.health_agent_prompts,
                                                incomplete_treatment_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Health Agents Prompts Core Incomplete Treatment Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    No Dental Score Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.health_agent_prompts.no_dental_score_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            health_agent_prompts: {
                                                ...formData.health_agent_prompts,
                                                no_dental_score_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Health Agents Prompts Core No Dental Score Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Need to Verify Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.health_agent_prompts.need_to_verify_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            health_agent_prompts: {
                                                ...formData.health_agent_prompts,
                                                need_to_verify_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Health Agents Prompts Core Need to Verify Section..."
                                />
                            </Stack>
                        </Box>

                        {/* Moderator Prompts */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Moderator Prompts
                            </Text>
                            <Stack spacing={3}>
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Base Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.moderator_prompts.base_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            moderator_prompts: {
                                                ...formData.moderator_prompts,
                                                base_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Moderator Prompts Base Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Grounding Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.moderator_prompts.grounding_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            moderator_prompts: {
                                                ...formData.moderator_prompts,
                                                grounding_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Moderator Prompts Core Responsibility Section..."
                                />
                            </Stack>
                        </Box>

                        {/* Summarizer Prompts */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Summarizer Prompts
                            </Text>
                            <Stack spacing={3}>
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Base Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.summarizer_prompts.base_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            summarizer_prompts: {
                                                ...formData.summarizer_prompts,
                                                base_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Summarizer Prompts Base Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Grounding Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.summarizer_prompts.edge_case_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            summarizer_prompts: {
                                                ...formData.summarizer_prompts,
                                                edge_case_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Summarizer Prompts Edge Case Section..."
                                />
                            </Stack>
                        </Box>

                        {/* Business Router Prompts */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Business Router Prompts
                            </Text>
                            <Stack spacing={3}>
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Main Prompt Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.business_router_prompts.main_prompt_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            business_router_prompts: {
                                                ...formData.business_router_prompts,
                                                main_prompt_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Business Router Prompts Main Prompt Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Confirm Transfer Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.business_router_prompts.confirm_transfer_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            business_router_prompts: {
                                                ...formData.business_router_prompts,
                                                confirm_transfer_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Business Router Prompts Confirm Transfer Section..."
                                />
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                    Clarify Business Section
                                </Text>
                                <Textarea
                                    minH="100px"
                                    resize="vertical"
                                    value={formData.business_router_prompts.clarify_business_section}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            business_router_prompts: {
                                                ...formData.business_router_prompts,
                                                clarify_business_section: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder="Business Router Prompts Clarify Business Section..."
                                />
                            </Stack>
                        </Box>

                    </Stack>
                )}
            </Box>
        </Box>

    );
};

export default VAVPrompts;